import { BASE_URL, COOKIE_TOKEN_KEY } from './Constants';
import { getTokenFromCookies } from './helpers';
import {
    Order,
    Product,
    Table,
    User,
    TableEvent,
    WorkingHoursType,
    FinalizeOrderType,
    Reservation
} from './types';

const checkIfAuthorized = (res: Response) => {
    // unauthorized
    if (res.status === 401) {
        document.cookie = `${COOKIE_TOKEN_KEY}=`;
        throw Error('Something went wrong');
    }
};

const getWithToken = (url: string) =>
    fetch(`${BASE_URL}/${url}`, {
        headers: {
            Authorization: `Bearer ${getTokenFromCookies()}`
        }
    }).then(res => {
        checkIfAuthorized(res);

        if (res.status === 200) {
            return res.json();
        }

        return res.text();
    });

const postWithToken = (url: string, data: Record<string, unknown>) =>
    fetch(`${BASE_URL}/${url}`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            Authorization: `Bearer ${getTokenFromCookies()}`,
            'Content-Type': 'application/json'
        }
    }).then(res => {
        checkIfAuthorized(res);

        if (res.status !== 200) {
            return res.text().then(e => {
                throw new Error(e);
            });
        }

        return res.text();
    });

export const login = (username: string, password: string, storeID: number) =>
    fetch(`${BASE_URL}/Account/Login`, {
        method: 'POST',
        body: JSON.stringify({
            username,
            password,
            storeID
        }),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            checkIfAuthorized(res);

            if (res.status === 200) {
                return res.json();
            }

            throw Error('Please check your credentials and try again');
        })
        .then(({ token }: { token: string }) => {
            document.cookie = `${COOKIE_TOKEN_KEY}=${token}`;
        });

export const getOpenOrders = (): Promise<Order[]> =>
    getWithToken('Orders/GetOpenOrders');

export const getNotDispatchedOrders = (): Promise<Order[]> =>
    getWithToken('Orders/GetPreparedButNotDispatchedOrders');

export const getProducts = (): Promise<Product[]> => getWithToken('Products');

export const getTables = (): Promise<Table[]> => getWithToken('Tables/GetAll');

export const getOccupiedTables = (): Promise<number[]> =>
    getWithToken('Tables/GetOccupied');

export const getUserAccount = (): Promise<User> =>
    getWithToken('Account/GetUserDetails');

export const getOpenTableEvents = (): Promise<TableEvent[]> =>
    getWithToken('TableEvents/GetOpen');

export const finalizeOrder = (orderId: number | number[], type: FinalizeOrderType) =>
    postWithToken('Orders/FinalizeOrder', {
        ...(Array.isArray(orderId) ? { orderItemIDs: orderId } : { orderID: orderId }),
        type
    });

export const getWorkingHours = (): Promise<any> =>
    getWithToken('WorkingHours/GetTodays');

export const overrideWorkingHours = (overrideType: WorkingHoursType) =>
    postWithToken('WorkingHours/AddOverride', {
        overrideType,
        dateTime: new Date().toISOString()
    });

export const setEventTableSeen = (id: number): Promise<any> =>
    postWithToken('TableEvents/SetAsSeen', { id });

export const getTableOrder = (tableId: number): Promise<Order> =>
    getWithToken(`Orders/GetActiveOrder?tableID=${tableId}`);

export const getReservations = (date: string): Promise<Reservation[]> =>
    getWithToken(`Reservations?date=${date}`);

export const confirmReservation = (reservationID: number) =>
    postWithToken('Reservations/StoreConfirmationInternal', {
        reservationID
    });
